import { useCallback, useEffect, useMemo, useState } from 'react';
import GridTable from '../../table/GridTable';
import { ColDef } from 'ag-grid-community';
import apiClient from '../../../service/apiClient';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { useFranchiseStore } from '../../../store/useFranchise';
import ConfirmDialog from '../../modal/ConfirmDialog';
import './CouponSendPage.css';

interface SendHistory {
  id: number;
  couponName: string;
  linkUrl: string;
  totalCount: number;
  successCount: number;
  failCount: number;
  totalCost: number;
  createdAt: string;
}

interface SendCountResponse {
  count: number;
  estimatedCost: number;
}

const getTomorrowExpiryLabel = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}.${month}.${day}`;
};

const CouponSendPage = () => {
  const myfranchise = useFranchiseStore(state => state.myfranchise);
  const [couponName, setCouponName] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [targetCount, setTargetCount] = useState(0);
  const [estimatedCost, setEstimatedCost] = useState(0);
  const [sending, setSending] = useState(false);
  const [historyList, setHistoryList] = useState<SendHistory[]>([]);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pageSize = 10;

  const fetchTargetCount = useCallback(async () => {
    try {
      const response = await apiClient.get<SendCountResponse>(
        '/fortune-lab/coupons/send-count'
      );
      if (response.data) {
        setTargetCount(response.data.count);
        setEstimatedCost(response.data.estimatedCost);
      }
    } catch (error) {
      console.error('Failed to fetch target count:', error);
      toast.error('발송 대상 인원을 조회하는데 실패했습니다.');
    }
  }, []);

  const fetchHistory = useCallback(async (page: number) => {
    try {
      const response = await apiClient.get<{
        content: SendHistory[];
        totalPages: number;
      }>('/fortune-lab/coupons/send-history', {
        params: { page, size: pageSize },
      });
      if (response.data) {
        setHistoryList(response.data.content);
        setHistoryTotalPages(response.data.totalPages);
      }
    } catch (error) {
      console.error('Failed to fetch send history:', error);
      toast.error('발송 히스토리를 조회하는데 실패했습니다.');
    }
  }, []);

  useEffect(() => {
    fetchTargetCount();
    fetchHistory(0);
  }, [fetchTargetCount, fetchHistory]);

  // 발송 버튼 클릭: 입력 검증 후 확인 다이얼로그 열기
  const handleSendClick = () => {
    if (!couponName.trim()) {
      toast.error('쿠폰명을 입력해주세요.');
      return;
    }
    if (!linkUrl.trim()) {
      toast.error('링크 URL을 입력해주세요.');
      return;
    }
    if (targetCount === 0) {
      toast.error('발송 대상이 없습니다.');
      return;
    }
    setConfirmOpen(true);
  };

  // 다이얼로그에서 "발송" 확정 시 실제 API 호출
  const handleSendCoupons = async () => {
    setSending(true);

    try {
      const response = await apiClient.post<{
        totalCount: number;
        successCount: number;
        failCount: number;
        totalCost: number;
      }>('/fortune-lab/coupons/bulk-send', {
        couponName: couponName.trim(),
        linkUrl: linkUrl.trim(),
      });

      if (response.data) {
        const { totalCount, totalCost } = response.data;
        toast.success(
          `발송 완료!\n총 ${totalCount}명 / 사용 금액: ${totalCost}원`
        );
        setCouponName('');
        setLinkUrl('');
        fetchTargetCount();
        fetchHistory(0);
        setHistoryPage(0);
      }
    } catch (error: any) {
      console.error('Failed to send coupons:', error);
      toast.error(
        error.response?.data?.message || '쿠폰 발송에 실패했습니다.'
      );
    } finally {
      setSending(false);
    }
  };


  const historyColumns: ColDef<SendHistory>[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        width: 80,
      },
      {
        field: 'couponName',
        headerName: '쿠폰명',
        flex: 2,
      },
      {
        field: 'totalCount',
        headerName: '발송 건수',
        width: 120,
        valueFormatter: (params) => `${params.value}명`,
      },
      {
        field: 'totalCost',
        headerName: '사용 금액',
        width: 120,
        valueFormatter: (params) => `${params.value}원`,
      },
      {
        field: 'createdAt',
        headerName: '발송 일시',
        flex: 1.5,
        valueFormatter: (params) => {
          if (!params.value) return '';
          const date = new Date(params.value);
          return date.toLocaleString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          });
        },
      },
    ],
    []
  );

  const handlePreviousPage = () => {
    if (historyPage > 0) {
      const newPage = historyPage - 1;
      setHistoryPage(newPage);
      fetchHistory(newPage);
    }
  };

  const handleNextPage = () => {
    if (historyPage < historyTotalPages - 1) {
      const newPage = historyPage + 1;
      setHistoryPage(newPage);
      fetchHistory(newPage);
    }
  };

  const expiryPreview = getTomorrowExpiryLabel();
  const previewReceiver = '고객';
  const previewCouponName = couponName.trim() || '특별 쿠폰';
  const previewLink = linkUrl.trim() ? (linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`) : '';
  const previewSender = myfranchise?.bizProfileName?.trim() || myfranchise?.companyName?.trim() || '사주톡톡';

  const handlePreviewLinkClick = useCallback(() => {
    if (!previewLink) {
      return;
    }
    window.open(previewLink, '_blank', 'noopener');
  }, [previewLink]);

  return (
    <div className="coupon-send-page">
      {/* 상단 영역: 쿠폰 정보 입력 + 미리보기 */}
      <div className="coupon-send-top">
        {/* 왼쪽: 쿠폰 정보 입력 */}
        <div className="coupon-send-form">
          <h3 className="!text-lg !font-semibold !mb-4">쿠폰 정보 입력</h3>

          <div className="!grid !grid-cols-1 md:!grid-cols-2 !gap-4 !mb-4">
            <div>
              <label className="!block !text-sm !font-medium !text-gray-700 !mb-2">
                쿠폰명 <span className="!text-red-500">*</span>
              </label>
              <input
                type="text"
                className="!w-full !px-3 !py-2 !border !border-gray-300 !rounded-md focus:!outline-none focus:!ring-2 focus:!ring-blue-500"
                value={couponName}
                onChange={(e) => setCouponName(e.target.value)}
                placeholder="예: 신규 가입 할인 쿠폰"
                disabled={sending}
              />
            </div>
            <div>
              <label className="!block !text-sm !font-medium !text-gray-700 !mb-2">
                링크 URL <span className="!text-red-500">*</span>
              </label>
              <input
                type="text"
                className="!w-full !px-3 !py-2 !border !border-gray-300 !rounded-md focus:!outline-none focus:!ring-2 focus:!ring-blue-500"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com/coupon"
                disabled={sending}
              />
            </div>
          </div>

          <div className="!bg-blue-50 !border !border-blue-200 !rounded-md !p-4 !mb-4">
            <div>
              <p className="!text-sm !text-gray-600 !mb-2">
                중복된 전화번호일 경우 대상에서 제외됩니다
              </p>
              <p className="!text-sm !text-gray-700">
                <span className="!font-semibold !text-blue-900">발송 대상:</span>{' '}
                <span className="!text-lg !font-bold !text-blue-600">{targetCount}명</span>
              </p>
              <p className="!text-sm !text-gray-700 !mt-1">
                <span className="!font-semibold !text-blue-900">예상 비용:</span>{' '}
                <span className="!text-lg !font-bold !text-blue-600">{estimatedCost}원</span>
                <span className="!text-xs !text-gray-500 !ml-2">(1건당 9원)</span>
              </p>
            </div>
          </div>

          {sending && (
            <div className="!mb-4">
              <div className="!bg-gray-200 !rounded-full !h-3 !overflow-hidden">
                <div className="!bg-blue-600 !h-full !rounded-full !animate-pulse" style={{ width: '100%' }}></div>
              </div>
              <p className="!text-center !text-sm !text-gray-600 !mt-2">
                쿠폰을 발송하고 있습니다...
              </p>
            </div>
          )}

          <button
            className="!w-full !bg-blue-600 hover:!bg-blue-700 disabled:!bg-gray-400 !text-white !font-semibold !py-3 !px-6 !rounded-md !transition-colors"
            onClick={handleSendClick}
            disabled={sending || targetCount === 0}
          >
            {sending ? '발송 중...' : '쿠폰 발송하기'}
          </button>
        </div>

        {/* 오른쪽: 미리보기 */}
        <div className="coupon-send-preview">
          <h3 className="!text-lg !font-semibold !mb-4">쿠폰 알림톡 미리보기</h3>
          <div className="alimtalk-preview">
            <div className="alimtalk-header">{previewSender}</div>
            <div className="alimtalk-body">
              <p>💡회원님을 위한 특별 쿠폰</p>
              <p>📜{previewCouponName}이 곧 사라져요!</p>
              <br />
              <p>안녕하세요 {previewReceiver}님</p>
              <p>{previewSender}입니다.</p>
              <br />
              <p>{previewReceiver}님을 위한</p>
              <p>{previewCouponName}이 남아있어요.</p>
              <p>바로 확인해보세요🎯</p>
              <br />
              <p>🔑할인 쿠폰 사용법</p>
              <p>▶ 쿠폰명 : {previewCouponName}</p>
              <p>▶ 유효기간 : {expiryPreview}</p>
              <p>▶ 사용방법 : 아래 비밀 링크에서 신청 시 적용</p>
            </div>
            <div className="alimtalk-footer">
              <button
                type="button"
                className="alimtalk-button"
                onClick={handlePreviewLinkClick}
                disabled={!previewLink}
              >
                쿠폰 확인하기
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 하단: 발송 히스토리 */}
      <div className="coupon-send-history">
        <h3 className="!text-lg !font-semibold !mb-4">발송 히스토리</h3>
        <div className="history-table-container">
          <GridTable<SendHistory>
            rowData={historyList}
            columnDefs={historyColumns}
            height={'100%'}
          />
        </div>

        {historyTotalPages > 1 && (
          <div className="!flex !items-center !justify-center !gap-4 !mt-4">
            <button
              onClick={handlePreviousPage}
              disabled={historyPage === 0}
              className="!px-3 !py-1 !text-sm !border !border-gray-300 !rounded disabled:!opacity-50 disabled:!cursor-not-allowed hover:!bg-gray-50"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="!mr-1" />
              이전
            </button>
            <span className="!text-sm !text-gray-600">
              {historyPage + 1} / {historyTotalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={historyPage >= historyTotalPages - 1}
              className="!px-3 !py-1 !text-sm !border !border-gray-300 !rounded disabled:!opacity-50 disabled:!cursor-not-allowed hover:!bg-gray-50"
            >
              다음
              <FontAwesomeIcon icon={faChevronRight} className="!ml-1" />
            </button>
          </div>
        )}
      </div>

      {/* 쿠폰 발송 확인 다이얼로그 */}
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleSendCoupons}
        title="쿠폰 발송 확인"
        message=""
        confirmText="발송"
        cancelText="취소"
        type="warning"
        messageNode={
          <div className="!flex !flex-col !gap-3 !text-left">
            <div>
              <div className="!text-xs !text-gray-500">쿠폰명</div>
              <div className="!text-sm !font-medium !text-gray-900">{couponName.trim()}</div>
            </div>

            <div>
              <div className="!text-xs !text-gray-500 !mb-1">링크 URL</div>
              <div className="!flex !items-center !gap-2">
                <span className="!text-sm !text-gray-900 !break-all !flex-1">
                  {linkUrl.trim()}
                </span>
                <a
                  href={linkUrl.trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="!shrink-0 !inline-flex !items-center !gap-1.5 !px-3 !py-2 !text-sm !font-semibold !text-white !bg-blue-600 hover:!bg-blue-700 !rounded-md !shadow-sm !whitespace-nowrap !transition-colors"
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                  링크 확인
                </a>
              </div>
              <div className="!mt-2 !p-2 !bg-red-50 !border !border-red-200 !rounded">
                <p className="!text-xs !font-semibold !text-red-700 !leading-relaxed">
                  ⚠️ 발송 전 반드시 위 <span className="!underline">"링크 확인"</span> 버튼을 눌러
                  새 탭에서 정상적으로 열리는지 확인하세요.
                </p>
              </div>
            </div>

            <div className="!border-t !pt-3 !flex !justify-between !items-center">
              <span className="!text-sm !text-gray-700">발송 대상</span>
              <span className="!text-sm !font-semibold !text-gray-900">
                {targetCount.toLocaleString()}명
              </span>
            </div>
            <div className="!flex !justify-between !items-center">
              <span className="!text-sm !text-gray-700">예상 비용</span>
              <span className="!text-sm !font-semibold !text-amber-600">
                {estimatedCost.toLocaleString()}원
              </span>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default CouponSendPage;
